import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { formatBRL, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { ScreenSpinner } from "@/components/ui/Spinner";
import { StatTile } from "@/components/ui/StatTile";
import { AdminHeader, errMessage } from "./shared";

type TransactionType =
  | "deposit"
  | "withdraw"
  | "bet"
  | "prize"
  | "bonus"
  | "credit";

type TxRow = {
  id: string;
  type: TransactionType;
  amount: number;
  userId: string;
  userName: string;
  userNickname: string | null;
  tournamentId: string | null;
  tournamentName: string | null;
  description: string | null;
  createdAt: string;
};

type TxResponse = {
  transactions: TxRow[];
  totals: { credits: number; debits: number; net: number };
  total: number;
  hasMore: boolean;
};

const TYPE_LABEL: Record<TransactionType, string> = {
  deposit: "Depósito",
  withdraw: "Saque",
  bet: "Aposta",
  prize: "Prêmio",
  bonus: "Ajuste",
  credit: "Crédito",
};

const TYPE_TONE: Record<
  TransactionType,
  "success" | "accent" | "muted" | "destructive"
> = {
  deposit: "success",
  withdraw: "destructive",
  bet: "muted",
  prize: "success",
  bonus: "accent",
  credit: "accent",
};

const PAGE_SIZE = 50;

const inputClass =
  "w-full rounded-(--radius-field) border border-input bg-card px-4 py-3 text-sm text-foreground outline-none transition-colors focus:border-tint";

export default function AdminExtratoPage() {
  const [month, setMonth] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");

  const [rows, setRows] = useState<TxRow[] | null>(null);
  const [totals, setTotals] = useState<TxResponse["totals"] | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const buildQuery = useCallback(
    (offset: number) => {
      const params = new URLSearchParams();
      if (month) params.set("month", month);
      if (type) params.set("type", type);
      if (qDebounced) params.set("q", qDebounced);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      return params.toString();
    },
    [month, type, qDebounced],
  );

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    apiFetch<TxResponse>(`/api/admin/transactions?${buildQuery(0)}`)
      .then((data) => {
        if (cancelled) return;
        setRows(data.transactions);
        setTotals(data.totals);
        setTotal(data.total);
        setHasMore(data.hasMore);
      })
      .catch((err) => {
        if (!cancelled) setError(errMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [buildQuery]);

  const loadMore = async () => {
    if (!rows || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await apiFetch<TxResponse>(
        `/api/admin/transactions?${buildQuery(rows.length)}`,
      );
      setRows([...rows, ...data.transactions]);
      setHasMore(data.hasMore);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 px-5 pt-6">
      <AdminHeader
        title="Extrato Geral"
        caption={`${total} movimentação${total === 1 ? "" : "ões"}`}
      />

      {totals && (
        <div className="flex gap-3">
          <StatTile label="Entradas" value={formatBRL(totals.credits)} />
          <StatTile label="Saídas" value={formatBRL(totals.debits)} />
          <StatTile label="Líquido" value={formatBRL(totals.net)} emphasis />
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={inputClass}
            aria-label="Período"
          />
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className={inputClass}
            aria-label="Tipo"
          >
            <option value="">Todos os tipos</option>
            {(Object.keys(TYPE_LABEL) as TransactionType[]).map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="relative">
          <Search
            size={16}
            className="absolute top-1/2 left-4 -translate-y-1/2 text-muted-foreground"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por jogador"
            className={`${inputClass} pl-10`}
          />
        </div>
      </div>

      {error && (
        <Card className="p-4 text-center text-sm text-[#ffb4ad]">{error}</Card>
      )}
      {!rows && !error && <ScreenSpinner />}

      <div className="flex flex-col gap-2 pb-6">
        {rows?.map((tx) => (
          <Card key={tx.id} className="flex items-center gap-3 p-3.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">
                {tx.userNickname ? `@${tx.userNickname}` : tx.userName}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {tx.description ?? TYPE_LABEL[tx.type]}
                {tx.tournamentName && ` · ${tx.tournamentName}`}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {formatDateTime(tx.createdAt)}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <p
                className={`text-sm font-bold ${
                  tx.amount >= 0 ? "text-tint" : "text-[#ffb4ad]"
                }`}
              >
                {tx.amount >= 0 ? "+" : ""}
                {formatBRL(tx.amount)}
              </p>
              <Chip tone={TYPE_TONE[tx.type]}>{TYPE_LABEL[tx.type]}</Chip>
            </div>
          </Card>
        ))}

        {rows && rows.length === 0 && (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            Nenhuma movimentação no filtro atual.
          </Card>
        )}

        {hasMore && (
          <Button
            title="Carregar mais"
            variant="secondary"
            loading={loadingMore}
            onClick={() => void loadMore()}
            className="h-11 text-sm"
          />
        )}
      </div>
    </div>
  );
}
